// Field Executive — Invoice Requests (client, 2026-10-07). A field executive
// cannot make an invoice: they raise a request for one of their own contacts;
// Super Admin / Admin / Department Administrator approve or reject it; once
// Accounts issues the invoice it can be downloaded here.
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Download, Eye, FileText, Paperclip, Plus, Search, Trash2, X } from 'lucide-react';
import { Spinner } from '@/shared/components/ui/Spinner';
import {
  errMsg, inr, invoiceDownloadUrl, searchMyContacts, searchProducts, STATUS, uploadFile, useFieldRequests, useRaiseRequest,
  type FieldRequestRow, type RaiseRequestBody,
} from '../api';

const TABS: Array<{ key: string; label: string }> = [
  { key: '', label: 'All' }, { key: 'requested', label: 'Waiting' }, { key: 'approved', label: 'Approved' }, { key: 'sent', label: 'Issued' }, { key: 'rejected', label: 'Rejected' },
];

export function FieldInvoiceRequestsPage() {
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState('');
  const { data, isLoading } = useFieldRequests(tab);
  const raising = params.get('new') === '1';
  const close = () => { params.delete('new'); setParams(params, { replace: true }); };

  return (
    <div className="mx-auto max-w-3xl space-y-4 pb-24">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-on-surface">Invoice Requests</h1>
          <p className="text-xs text-on-surface-variant">Raise a request — Super Admin, Admin or Department Administrator approves it, then you can download the invoice.</p>
        </div>
        <button type="button" onClick={() => setParams({ new: '1' })} className="inline-flex shrink-0 items-center gap-1.5 rounded-xl bg-primary px-3.5 py-2 text-sm font-semibold text-on-primary"><Plus className="h-4 w-4" /> New request</button>
      </div>
      <div className="flex gap-1 overflow-x-auto rounded-xl bg-muted p-1">
        {TABS.map((t) => (
          <button key={t.key} type="button" onClick={() => setTab(t.key)} className={`shrink-0 rounded-lg px-3 py-1.5 text-xs font-semibold ${tab === t.key ? 'bg-card text-success-text shadow-xs' : 'text-on-surface-variant'}`}>{t.label}</button>
        ))}
      </div>
      {isLoading ? <div className="flex justify-center py-16"><Spinner /></div> : !data?.length ? (
        <p className="rounded-2xl border border-dashed border-border bg-card p-10 text-center text-sm text-on-surface-variant">No requests here yet.</p>
      ) : (
        <ul className="space-y-3">{data.map((r) => <RequestCard key={r.id} r={r} />)}</ul>
      )}
      {raising && <RaiseRequestSheet onClose={close} />}
    </div>
  );
}

function RequestCard({ r }: { r: FieldRequestRow }) {
  const st = STATUS[r.status];
  return (
    <li className="rounded-card border border-border bg-card p-4 shadow-xs">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-bold text-on-surface">{r.clientName}</p>
          <p className="text-xs text-on-surface-variant">{r.number} · {new Date(r.requestedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
        </div>
        <span className={`shrink-0 rounded-full px-2 py-0.5 text-data-xs font-semibold ring-1 ${st.cls}`}>{st.label}</span>
      </div>
      <ul className="mt-2 space-y-0.5 text-xs text-on-surface-variant">
        {(r.items || []).slice(0, 4).map((it, i) => <li key={i}>{it.name}{it.packSize ? ` (${it.packSize})` : ''} × {it.quantity}{it.rate ? ` @ ${inr(it.rate)}` : ''}</li>)}
        {(r.items || []).length > 4 && <li className="text-on-surface-subtle">+{(r.items || []).length - 4} more</li>}
      </ul>
      <div className="mt-3 flex items-center justify-between">
        <span className="text-sm font-bold text-on-surface">{r.total ? inr(r.total) : <span className="text-xs font-normal text-on-surface-subtle">Rate set at approval</span>}</span>
        {r.status === 'sent' && (
          <div className="flex gap-2">
            <a href={invoiceDownloadUrl(r.id, true)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-lg border border-border px-2.5 py-1.5 text-xs font-semibold text-on-surface-variant"><Eye className="h-3.5 w-3.5" /> View</a>
            <a href={invoiceDownloadUrl(r.id)} className="inline-flex items-center gap-1 rounded-lg bg-success/10 px-2.5 py-1.5 text-xs font-semibold text-white"><Download className="h-3.5 w-3.5" /> {r.invoiceNumber || 'Invoice'}</a>
          </div>
        )}
      </div>
      {r.status === 'rejected' && r.rejectReason && <p className="mt-2 rounded-lg bg-danger/10 px-3 py-2 text-xs text-danger-text">Rejected: {r.rejectReason}</p>}
    </li>
  );
}

type Line = { key: number; productId?: string; name: string; sku?: string; packSize?: string; hsnSac?: string; quantity: string; rate: string };
let lineKey = 1;

function RaiseRequestSheet({ onClose }: { onClose: () => void }) {
  const raise = useRaiseRequest();
  const [contactQ, setContactQ] = useState('');
  const [contacts, setContacts] = useState<Awaited<ReturnType<typeof searchMyContacts>>>([]);
  const [contact, setContact] = useState<{ id: string; label: string } | null>(null);
  const [productQ, setProductQ] = useState('');
  const [products, setProducts] = useState<Awaited<ReturnType<typeof searchProducts>>>([]);
  const [lines, setLines] = useState<Line[]>([]);
  const [notes, setNotes] = useState('');
  const [address, setAddress] = useState('');
  const [files, setFiles] = useState<Array<{ url: string; name: string }>>([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (contact) return undefined;
    const t = setTimeout(() => { searchMyContacts(contactQ).then(setContacts).catch(() => setContacts([])); }, 250);
    return () => clearTimeout(t);
  }, [contactQ, contact]);
  useEffect(() => {
    const t = setTimeout(() => { searchProducts(productQ).then(setProducts).catch(() => setProducts([])); }, 250);
    return () => clearTimeout(t);
  }, [productQ]);

  const addLine = (p?: { id: string; name: string; sku: string; packSize: string; hsnSac: string }) => {
    setLines((ls) => [...ls, p ? { key: lineKey++, productId: p.id, name: p.name, sku: p.sku, packSize: p.packSize, hsnSac: p.hsnSac, quantity: '1', rate: '' } : { key: lineKey++, name: productQ.trim(), quantity: '1', rate: '' }]);
    setProductQ(''); setProducts([]);
  };
  const setLine = (key: number, patch: Partial<Line>) => setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  const problems = useMemo(() => {
    const p: string[] = [];
    if (!contact) p.push('choose the client');
    if (!lines.length) p.push('add at least one product');
    if (lines.some((l) => !l.name.trim() || !(Number(l.quantity) > 0))) p.push('every product needs a quantity');
    return p;
  }, [contact, lines]);

  const submit = async () => {
    if (problems.length || !contact) return;
    const body: RaiseRequestBody = {
      contactId: contact.id, notes, deliveryAddress: address, poAttachments: files,
      items: lines.map((l) => ({ productId: l.productId, name: l.name.trim(), sku: l.sku, packSize: l.packSize, hsnSac: l.hsnSac, quantity: Number(l.quantity), rate: l.rate === '' ? '' : Number(l.rate) })),
    };
    try {
      const r = await raise.mutateAsync(body);
      toast.success(`Request ${r.number} sent for approval.`);
      onClose();
    } catch (e) { toast.error(errMsg(e, 'Could not raise the request.')); }
  };

  const field = 'w-full rounded-xl border border-border bg-card px-3 py-2.5 text-sm outline-none focus:border-success/30 focus:ring-2 focus-visible:ring-ring';
  return (
    <div className="fixed inset-0 z-[1100] flex items-end justify-center bg-slate-900/50 sm:items-center sm:p-4" onClick={onClose}>
      <div className="flex max-h-[94vh] w-full max-w-lg flex-col overflow-hidden rounded-t-3xl bg-card shadow-2xl sm:rounded-3xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <h2 className="flex items-center gap-2 text-base font-bold text-on-surface"><FileText className="h-5 w-5 text-success-text" /> New invoice request</h2>
          <button type="button" onClick={onClose} aria-label="Close" className="rounded-lg p-1 text-on-surface-subtle hover:bg-muted"><X className="h-5 w-5" /></button>
        </div>
        <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
          {/* Client */}
          <div>
            <p className="mb-1 text-xs font-semibold text-on-surface-variant">Client <span className="text-danger-text">*</span></p>
            {contact ? (
              <div className="flex items-center justify-between rounded-xl border border-success/30 bg-success/10 px-3 py-2.5">
                <span className="text-sm font-semibold text-success-text">{contact.label}</span>
                <button type="button" onClick={() => setContact(null)} className="text-xs font-semibold text-success-text">Change</button>
              </div>
            ) : (
              <>
                <div className="relative"><Search className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-on-surface-subtle" /><input className={`${field} pl-9`} value={contactQ} onChange={(e) => setContactQ(e.target.value)} placeholder="Search your contacts…" autoFocus /></div>
                <ul className="mt-1 max-h-48 overflow-y-auto rounded-xl border border-border">
                  {contacts.map((c) => (
                    <li key={c._id}><button type="button" onClick={() => setContact({ id: c._id, label: c.company && c.company !== c.name ? `${c.company} — ${c.name}` : c.name })} className="block w-full px-3 py-2 text-left text-sm hover:bg-muted">
                      <span className="font-semibold text-on-surface">{c.company || c.name}</span>{c.company && c.company !== c.name ? <span className="text-on-surface-variant"> · {c.name}</span> : null}{c.city ? <span className="text-xs text-on-surface-subtle"> · {c.city}</span> : null}
                    </button></li>
                  ))}
                  {!contacts.length && <li className="px-3 py-3 text-xs text-on-surface-subtle">Only your own and assigned contacts are shown.</li>}
                </ul>
              </>
            )}
          </div>

          {/* Products */}
          <div>
            <p className="mb-1 text-xs font-semibold text-on-surface-variant">Products <span className="text-danger-text">*</span></p>
            <div className="relative"><Search className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-on-surface-subtle" /><input className={`${field} pl-9`} value={productQ} onChange={(e) => setProductQ(e.target.value)} placeholder="Search product name or SKU…" /></div>
            {(products.length > 0 || productQ.trim().length >= 2) && (
              <ul className="mt-1 max-h-48 overflow-y-auto rounded-xl border border-border">
                {products.map((p) => (
                  <li key={p.id}><button type="button" onClick={() => addLine(p)} className="block w-full px-3 py-2 text-left text-sm hover:bg-muted"><span className="font-semibold text-on-surface">{p.name}</span>{p.packSize ? <span className="text-xs text-on-surface-variant"> · {p.packSize}</span> : null}</button></li>
                ))}
                {productQ.trim().length >= 2 && <li><button type="button" onClick={() => addLine()} className="block w-full px-3 py-2 text-left text-xs font-semibold text-success-text hover:bg-success/10">+ Add “{productQ.trim()}” as typed</button></li>}
              </ul>
            )}
            <ul className="mt-2 space-y-2">
              {lines.map((l) => (
                <li key={l.key} className="rounded-xl border border-border p-3">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-semibold text-on-surface">{l.name}{l.packSize ? <span className="text-xs font-normal text-on-surface-variant"> · {l.packSize}</span> : null}</p>
                    <button type="button" onClick={() => setLines((ls) => ls.filter((x) => x.key !== l.key))} aria-label="Remove" className="text-on-surface-subtle hover:text-danger-text"><Trash2 className="h-4 w-4" /></button>
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <label className="text-data-xs font-semibold text-on-surface-variant">Quantity *<input className={field} inputMode="decimal" value={l.quantity} onChange={(e) => setLine(l.key, { quantity: e.target.value })} /></label>
                    <label className="text-data-xs font-semibold text-on-surface-variant">Rate ₹ (optional)<input className={field} inputMode="decimal" value={l.rate} onChange={(e) => setLine(l.key, { rate: e.target.value })} placeholder="Approver fills if blank" /></label>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <label className="block text-xs font-semibold text-on-surface-variant">Delivery address (if different)<textarea className={field} rows={2} value={address} onChange={(e) => setAddress(e.target.value)} /></label>
          <label className="block text-xs font-semibold text-on-surface-variant">Notes for the approver<textarea className={field} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Payment terms, urgency, anything else…" /></label>

          <div>
            <p className="mb-1 text-xs font-semibold text-on-surface-variant">Customer PO (photo / PDF, optional)</p>
            <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-xl border border-dashed border-border px-3 py-2 text-xs font-semibold text-on-surface-variant hover:border-success/30">
              <Paperclip className="h-4 w-4" /> {uploading ? 'Uploading…' : 'Attach'}
              <input type="file" accept="image/*,application/pdf" capture="environment" className="hidden" disabled={uploading} onChange={async (e) => {
                const f = e.target.files?.[0]; e.target.value = '';
                if (!f) return;
                setUploading(true);
                try { const up = await uploadFile(f); setFiles((x) => [...x, up]); } catch (err) { toast.error(errMsg(err, 'Upload failed.')); } finally { setUploading(false); }
              }} />
            </label>
            {files.map((f, i) => <p key={i} className="mt-1 flex items-center gap-2 text-xs text-on-surface-variant"><Paperclip className="h-3 w-3" />{f.name}<button type="button" className="text-danger-text" onClick={() => setFiles((x) => x.filter((_, j) => j !== i))}>remove</button></p>)}
          </div>
        </div>
        <div className="border-t border-border px-5 py-3">
          {problems.length > 0 && <p className="mb-2 text-xs text-warning-text">To send: {problems.join(', ')}.</p>}
          <button type="button" disabled={!!problems.length || raise.isPending} onClick={submit} className="w-full rounded-xl bg-success/10 py-3 text-sm font-bold text-white shadow-xs disabled:opacity-50">{raise.isPending ? 'Sending…' : 'Send for approval'}</button>
        </div>
      </div>
    </div>
  );
}

export default FieldInvoiceRequestsPage;
